import {Signal} from '../signals-screen/types';
import {DeviceFilterType} from '../../services/types';
import {DateRangeValue} from '../../utils/time';
import {ATTENTION_METRIC_NAME} from '../../shared/types/common';
import {PolarisDataDomain} from '../../services/polaris/model/polaris-domain.models';
export type HistoryMessage = {
  role: string;
  content: string;
}

export type FocusArea = {
  title: string;
  subQuestions: string[];
}

export type WebInsight = {
  insight: string;
  sources: {
    title: string,
    name: string,
    link: string
  }[];
}

export type WebInsights = WebInsight[];

export type FocusAreasMap = Record<string, { subQuestions: string[] }>;

export type WizardPayload = {
  type: string;
  focusAreas: Record<string, FocusArea>;
  brands: string[];
  competitors: string[];
  geos: string[];
  startDate: string;
  endDate: string;
};

export type AlertPayload = {
  alert_type: string;
  metric: string;
  change: {
    direction: string;
    percent: string;
  };
  date_before: string;
  date_after: string;
  brand_key: string;
}

export type WidgetContext = {
  name: DashboardWidget | SocialWidget;
  metrics: string[];
  myBrand: string;
  myCompetitors: string[];
  dateRange: DateRangeValue;
  countries: string[];
  device: DeviceFilterType;
}

export type CopilotMessagePayload = {
  requestId: string;
  userPrompt: string;
  sessionContext: {
    sessionId: string;
    customerName: string;
    customerBrandsList: string[];
  };
  workflowContext?: string;
  conversationHistory: HistoryMessage[];
  agencyId: string;
  debug?: boolean;
  wizard?: WizardPayload;
  alert?: AlertPayload;
  signal?: Signal;
  widgetContext?: WidgetContext;
}

export type CopilotMessageResponse = {
  requestId: string;
  sessionId: string;
  userId: string;
  status: string;
  timestamp: string;
  answer: {
    text: string;
    insights: string;
  };
  graph: graphSpec[];
  followUpQuestions: string[];
  creativesURLs: string[];
  originalResponse?:any;
  lambdaId?: string;
}

export type Wizard = {
    type:  string;
    focusAreas: FocusAreasMap;
    title: string;
    description: string;
    tileSvg: string;
    wizardSvg: string;
}

export type graphSpec = {
  title: string;
  type: 'bar'| 'line' | 'doughnut';
  url?: string;
  x: string[];
  series: graphSeries[];
  y_is_percentage?: boolean;
  x_axis: string;
  y_axis: string;
}

export type graphSeries = {
  name: string;
  y: number[];
  unit?: string;
}

export enum DashboardWidget {
  SPEND_ANALYSIS = 'Spend Analysis',
  CREATIVE_GALLERY = 'Creative Gallery',
  CREATIVE_GALLERY_COMPETITION = 'My Competition Creative Gallery',
  CREATIVE_GALLERY_MESSAGING = 'Creative Gallery Messaging',
  PERFORMANCE_MAP = 'Performance Map',
  PERFORMANCE_OVER_TIME = 'Performance Over Time',
  MY_COMPETITION_OVERVIEW = 'Overview',
  MY_BRAND_OVERVIEW = 'My Brand Overview',
  AUDIENCE_INTERESTS = 'Audience Interests',
  MY_BRAND_AUDIENCE_INTERESTS = 'My Brand Audience Interests',
  PERFORMANCE_BY_DEVICE = 'Performance by Device',
  SHARE_OF_VOICE = 'Share of Voice',
  AUDIENCE_REACH_BY_GENDER = 'Audience Reach by Gender',
  MY_BRAND_AUDIENCE_REACH_BY_GENDER = 'My Brand Audience Reach by Gender'
}

export enum SocialWidget {
  OVERVIEW = 'Overview',
  CREATIVE_GALLERY = 'Creative Gallery',
  SHARE_OF_VOICE = 'Share of Voice',
  PERFORMANCE_OVER_TIME = 'Performance Over Time',
  CHANNEL_DISTRIBUTION = 'Channel Distribution by Spend',
  AD_TYPE = 'Ad Type by Spend'
}

const CREATIVE_GALLERY_COMPETITORS_QUESTIONS = [
  "Which phrases are most frequently used in my competitors' ads?",
  "What factors are contributing to my competitor's top creative's success?",
  "What can I do to improve my creative effectiveness based on the competition?"
];

export const WIDGET_QUESTIONS_MAP = {
  [DashboardWidget.SPEND_ANALYSIS]: [
    "How does my spend efficiency compare against industry benchmarks?",
    "How can I scale my campaign CTR success, based on my competitions strategies?",
    "Where am I seeing the biggest gaps in efficiency vs. my competitors?",
    "What are the biggest drivers of inefficient spend in my current dataset?"
  ],
  [DashboardWidget.CREATIVE_GALLERY]: [
    `What are my most effective creatives based on CTR, ${ATTENTION_METRIC_NAME}, and impressions?`,
    "What can I do to improve my creative effectiveness based on the competition?",
    "What factors are contributing to the success of my top creatives?"
  ],
  [DashboardWidget.CREATIVE_GALLERY_COMPETITION]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.CREATIVE_GALLERY_MESSAGING]: CREATIVE_GALLERY_COMPETITORS_QUESTIONS,
  [DashboardWidget.PERFORMANCE_MAP]: [
    `Which states should my brand prioritize for expansion based on cost efficiency and performance (${ATTENTION_METRIC_NAME}, dwell time, CTR)?`,
    `Which states are my competitors seeing the most success in (highest CTR, highest ${ATTENTION_METRIC_NAME}, lowest CPM)?`,
    "Based on competitor success, which states should I target?",
    "Which of the countries I’m targeting are underperforming or overperforming compared to my competitors, and why?"
  ],
  [DashboardWidget.PERFORMANCE_OVER_TIME]: [
    "Are gaps versus competitors widening or narrowing over time?",
    "What explains the performance trends across the selected brands?",
  ],
  [DashboardWidget.MY_COMPETITION_OVERVIEW]: [
    `My brand’s CPM is higher than my competitors’, but my ${ATTENTION_METRIC_NAME} is lower. Why? How can I improve this?`,
    "My brand is achieving a higher CTR than my competitors with a lower CPM. Why? How can I scale this approach?",
    `Which audience segments generate the highest ${ATTENTION_METRIC_NAME} for my brand compared to my competitors?`
  ],
  [DashboardWidget.MY_BRAND_OVERVIEW]: [
    "What's the relationship between my CTR and TruAttention™ trends, and what does it suggest about my campaign quality?",
    "How does my Viewability compare to industry benchmarks, and what's limiting it?",
    "Where is my media spend working hardest, and where is it being wasted?",
    "Am I overpaying for placements that don't perform?"
  ],
  [DashboardWidget.AUDIENCE_INTERESTS]: [
    "Which competitors are most heavily investing in the subcategories within my primary audience interest category?",
    "Are the interests I'm leading in actually more engaging than the ones my competitors own?",
    "Which interests am I over-investing in relative to the engagement they deliver?",
    "Which interest categories show high engagement potential but low competitive presence, including mine?"
  ],
  [DashboardWidget.MY_BRAND_AUDIENCE_INTERESTS]: [
    "Which of my smaller audience interest segments might be worth testing further?",
    "Which audience interest segments show the highest attention or viewability for my brand?",
    "Is my largest interest segment also my best-performing one, or are they different?"
  ],
  [DashboardWidget.PERFORMANCE_BY_DEVICE]: [
    `Which competitor is achieving the highest ${ATTENTION_METRIC_NAME} score on mobile, and what ad sizes are they using?`,
    "Is any competitor gaining ground on a specific device over the last 90 days?",
    `What optimizations should I make to improve my ${ATTENTION_METRIC_NAME} score on the device where I am weakest?`
  ],
  [DashboardWidget.SHARE_OF_VOICE]: [
    "Which competitor has grown their share of voice the most over the past 90 days, and what's driving it?",
    "What strategies are supporting my competitors' share of voice presence?"
  ],
  [DashboardWidget.AUDIENCE_REACH_BY_GENDER]: [
    "Is the gender I'm reaching more heavily actually more engaged with my ads?",
    "Is any competitor actively shifting their gender targeting?"
  ],
  [DashboardWidget.MY_BRAND_AUDIENCE_REACH_BY_GENDER]: [
    "Which gender shows higher TruAttention™ for my brand?",
    "Which gender drives a better CTR for my brand?",
    "How do audience interests differ between men and women for my brand?",
    "Are there states where my gender reach skews differently than my overall average?"
  ]
}

export const SOCIAL_WIDGET_QUESTIONS_MAP: Record<SocialWidget, string[]> = {
  [SocialWidget.OVERVIEW]: [
    "My brand has a higher CPM than my competitors. What could explain this difference, and what are my options?"
  ],
  [SocialWidget.CREATIVE_GALLERY]: [
    "What ad formats are my competitors running most, and how does my creative mix compare?",
    "Which competitor is refreshing creatives most frequently, and what does their rotation strategy look like?",
    "What CTA strategies are my competitors using most — and what does that tell us about how they're positioning their message?",
    "What themes appear most in my competitors' social campaigns, and how do they differ from mine?"
  ],
  [SocialWidget.SHARE_OF_VOICE]: [
    "Which platform is my brand underweighting relative to competitors, and what's the scale of the gap?",
    "Which platform shows the biggest share of voice imbalance between me and the competition?"
  ],
  [SocialWidget.PERFORMANCE_OVER_TIME]: [
    "Are the spend gaps between my brand and competitors widening or narrowing over time?",
    "Which competitors spend consistently week over week, and which ones run in bursts? What does that tell us about their strategy?"
  ],
  [SocialWidget.CHANNEL_DISTRIBUTION]: [
    "Are my competitors reusing the same creatives across platforms, or tailoring content per channel?",
    "My competitors are investing in platforms I'm not present on. What's the case for or against following them?"
  ],
  [SocialWidget.AD_TYPE]: [
    "My competitors are allocating more budget to a different ad type than I am. What might be driving that choice?",
    "Which competitors are present on the smaller platforms, and are they testing or committing real budget there?",
    "Has any competitor meaningfully shifted their channel mix over the selected period?",
    "Are there ad formats my competitors are using that I'm not investing in at all? What am I potentially missing?"
  ]
};

export interface AskPolarisWidgetConfig {
  name: DashboardWidget | SocialWidget;
  domain: PolarisDataDomain;
  questions: string[];
}

export const WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN = {
  SPEND_ANALYSIS:               { name: DashboardWidget.SPEND_ANALYSIS,               domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.SPEND_ANALYSIS] },
  CREATIVE_GALLERY:             { name: DashboardWidget.CREATIVE_GALLERY,             domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.CREATIVE_GALLERY] },
  CREATIVE_GALLERY_COMPETITION: { name: DashboardWidget.CREATIVE_GALLERY,             domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.CREATIVE_GALLERY_COMPETITION] },
  CREATIVE_GALLERY_MESSAGING:   { name: DashboardWidget.CREATIVE_GALLERY_MESSAGING,   domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.CREATIVE_GALLERY_MESSAGING] },
  PERFORMANCE_MAP:              { name: DashboardWidget.PERFORMANCE_MAP,              domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.PERFORMANCE_MAP] },
  PERFORMANCE_OVER_TIME:        { name: DashboardWidget.PERFORMANCE_OVER_TIME,        domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.PERFORMANCE_OVER_TIME] },
  MY_COMPETITION_OVERVIEW:      { name: DashboardWidget.MY_COMPETITION_OVERVIEW,      domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.MY_COMPETITION_OVERVIEW] },
  MY_BRAND_OVERVIEW:            { name: DashboardWidget.MY_COMPETITION_OVERVIEW,      domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.MY_BRAND_OVERVIEW] },
  AUDIENCE_INTERESTS:           { name: DashboardWidget.AUDIENCE_INTERESTS,           domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.AUDIENCE_INTERESTS] },
  MY_BRAND_AUDIENCE_INTERESTS:  { name: DashboardWidget.AUDIENCE_INTERESTS,           domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.MY_BRAND_AUDIENCE_INTERESTS] },
  PERFORMANCE_BY_DEVICE:        { name: DashboardWidget.PERFORMANCE_BY_DEVICE,        domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.PERFORMANCE_BY_DEVICE] },
  SHARE_OF_VOICE:               { name: DashboardWidget.SHARE_OF_VOICE,               domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.SHARE_OF_VOICE] },
  AUDIENCE_REACH_BY_GENDER:     {name: DashboardWidget.AUDIENCE_REACH_BY_GENDER,      domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.AUDIENCE_REACH_BY_GENDER] },
  MY_BRAND_AUDIENCE_REACH_BY_GENDER: {name: DashboardWidget.AUDIENCE_REACH_BY_GENDER, domain: PolarisDataDomain.OPEN_WEB, questions: WIDGET_QUESTIONS_MAP[DashboardWidget.MY_BRAND_AUDIENCE_REACH_BY_GENDER] },
  SOCIAL_OVERVIEW:              { name: SocialWidget.OVERVIEW,             domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.OVERVIEW] },
  SOCIAL_CREATIVE_GALLERY:      { name: SocialWidget.CREATIVE_GALLERY,     domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.CREATIVE_GALLERY] },
  SOCIAL_SHARE_OF_VOICE:        { name: SocialWidget.SHARE_OF_VOICE,       domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.SHARE_OF_VOICE] },
  SOCIAL_PERFORMANCE_OVER_TIME: { name: SocialWidget.PERFORMANCE_OVER_TIME, domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.PERFORMANCE_OVER_TIME] },
  SOCIAL_CHANNEL_DISTRIBUTION:  { name: SocialWidget.CHANNEL_DISTRIBUTION, domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.CHANNEL_DISTRIBUTION] },
  SOCIAL_AD_TYPE:               { name: SocialWidget.AD_TYPE,              domain: PolarisDataDomain.SOCIAL, questions: SOCIAL_WIDGET_QUESTIONS_MAP[SocialWidget.AD_TYPE] },
} satisfies Record<string, AskPolarisWidgetConfig>;

export const ASK_POLARIS_SIDE_DRAWER_WIDTH = '700px';

export const POLARIS_HOME_SUGGESTED_QUESTIONS_AMOUNT: number = 3;
export const POLARIS_HOME_SUGGESTED_QUESTIONS = [
  "How can I improve my CTR based on what works for my competitors?",
  "How can I scale CTR gains without increasing CPM?",
  "What is currently limiting my CTR the most?",
  "Where am I overpaying relative to performance (CPM vs CTR)?",
  "Which of my creatives perform best by CTR, attention, and impressions?",
  "Which of my competitors' creatives perform best, and why?",
  "Which of my creatives underperform by CTR or CPM (with sufficient impressions)?",
  "How does a specific creative perform compared to similar creatives in size and volume?",
  "What creative optimizations would most likely improve my results?",
  "What factors contribute to the success of my top creative?",
  "What are my competitors doing better than I right now?",
  "Where do I outperform my competitors, and why?",
  "Which competitor strategies are driving the biggest performance gaps?",
  "Based on competitor success, which geographies should I prioritize next?",
  "Which audiences drive high attention but low engagement, and why?",
  "What audience segments should I scale based on current performance?",
  "Which targeting strategies are driving the strongest results for my competitors?",
  "Where is my targeting too broad or too narrow compared to competitors?",
  "How can I refine my targeting to improve efficiency without losing scale?",
  "How should I reallocate spend across audiences and targeting to improve results?"
];
